"use client";

import { ProductCard } from "~/components/storefront/product-card";
import type { ProductCardView } from "~/components/storefront/product-card";
import { cn } from "~/lib/utils";
import type { PlpView } from "~/lib/storefront/plp-params";

/**
 * P2-09 · The product grid/list. A semantic list (`<ul>`) of `<ProductCard>`s
 * — grid reflows 2 → 3 → 4 columns, list is a single stack of list-variant
 * rows. Rows are already mapped to `ProductCardView` (media keys → URLs)
 * server-side.
 *
 * P2-15: `priorityFirst` marks the FIRST card as the view's LCP image — the
 * PLP passes it only when the category has no banner, so each view preloads
 * exactly one image. The rest of the first row is in-viewport and fetches
 * immediately at layout without competing preloads.
 *
 * P18-19: the grid is the PLP's single client boundary. It never had a
 * directive, so with a client `<ProductCard>` every card used to cross on its
 * own, each paying its own `["$","$L..",key,{props}]` framing — the shape
 * P17A-08 measured as the more expensive one and fixed for the homepage block
 * but never for this grid. One boundary here pays that framing once for the
 * whole `products` array, which is what `InfiniteResults` (already a client
 * island, and the caller for pages 2+) was doing all along.
 *
 * Why a boundary at all, now that the card could render on the server: a server
 * component's markup is serialised into the inlined RSC payload as well as the
 * HTML, and a card is ~5.1 KB of markup built from ~1.1 KB of data. See
 * `docs/storefront-performance.md` § "Where a product card's bytes go".
 */
export function ProductGrid({
  products,
  view,
  priorityFirst = false,
}: {
  products: ProductCardView[];
  view: PlpView;
  priorityFirst?: boolean;
}) {
  return (
    <ul
      aria-label="Products"
      className={cn(
        view === "grid"
          ? "grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4"
          : "flex flex-col gap-4",
      )}
    >
      {products.map((product, i) => (
        <li key={product.id}>
          <ProductCard
            product={product}
            variant={view}
            priority={priorityFirst && i === 0}
          />
        </li>
      ))}
    </ul>
  );
}
